import { createContext, useContext, PropsWithChildren, useMemo, useState } from "react";
import { UsageStatsType, useHereAndNowFilterData, useUsageStats } from "@lib/logic";
import { useHereAndNowBuildingSelection, useHereAndNowFloorSelection, useHereAndNowTab } from "./hooks";
import { useFeatureToggle } from "@hooks";
import { getDeskAmenityIcon, getRoomEquipementIcon } from "@utils";
import { AreaWithSensorInfo, FacilityResource, MeetingRoom, Resource } from "@lib/store";

export type HereAndNowContextType = ReturnType<typeof useContextValue>;

const HereAndNowContext = createContext<HereAndNowContextType | undefined>(undefined);

const useContextValue = () => {
  const enabledFeatures = useFeatureToggle();
  const [buildingId, setBuildingId] = useHereAndNowBuildingSelection();
  const [selectedFloorId, setSelectedFloorId] = useHereAndNowFloorSelection(buildingId);
  const filterData = useHereAndNowFilterData({
    buildingId,
    floorId: selectedFloorId,
    enabledFeatures,
    getDeskAmenityIcon,
    getRoomEquipementIcon,
  });

  const tab = useHereAndNowTab();

  const [hoverRoomId, setHoverRoomId] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<MeetingRoom | AreaWithSensorInfo | Resource | null>(null);

  // Usage stats
  const usageStatsType = useMemo((): UsageStatsType => {
    switch (tab) {
      case "rooms":
        return "rooms";
      case "workspaces":
        return "workspaces";
      default:
        return "none";
    }
  }, [tab]);

  const usageStatsPerFloor = useUsageStats({
    buildingId,
    type: usageStatsType,
  });

  const hasAnyStats = Array.from(usageStatsPerFloor.values()).some(stats => stats !== null);

  return {
    tab,
    buildingId,
    setBuildingId,
    selectedFloorId,
    setSelectedFloorId,

    hoverRoomId,
    setHoverRoomId,
    selectedObject,
    setSelectedObject,

    filterData,
    hasAnyStats,
    usageStatsPerFloor,
  };
};

export const HereAndNowProvider = ({ children }: PropsWithChildren) => {
  const contextValue = useContextValue();

  return <HereAndNowContext.Provider value={contextValue}>{children}</HereAndNowContext.Provider>;
};

export const useHereAndNow = (): HereAndNowContextType => {
  const context = useContext(HereAndNowContext);
  if (context === undefined) {
    throw new Error("useHereAndNow must be used within a HereAndNowProvider");
  }
  return context;
};
